import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, ArrowLeft, CheckCircle2, MailWarning } from "lucide-react";

const fshort = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—");
const LABELS = { 1: "Décevant", 2: "Moyen", 3: "Bien", 4: "Très bien", 5: "Excellent" };

export default function PublicReview() {
  const { code, token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/public/reviews/${token}`).then((r) => setData(r.data)).catch((e) => setData({ error: formatApiError(e.response?.data?.detail) }));
  }, [token]);

  const submit = async () => {
    if (!rating) return toast.error("Choisissez une note de 1 à 5");
    setSending(true);
    try {
      await api.post(`/public/reviews/${token}`, { rating, comment });
      setData({ ...data, submitted: true, rating, comment });
      toast.success("Merci pour votre avis !");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (data?.error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="rv-error">
        <div className="max-w-md text-center"><MailWarning className="h-10 w-10 mx-auto text-[#FFD700] mb-3" />
          <div className="font-display font-black text-2xl">Lien indisponible</div><div className="text-sm text-white/60 mt-2">{data.error}</div>
          <Button variant="outline" className="mt-4 border-white/25 text-white hover:bg-white hover:text-black" onClick={() => navigate(`/sites/${code}`)} data-testid="rv-back">Retour au site</Button></div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-white/50 p-10" data-testid="rv-loading">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="public-review-page">
      <div className="max-w-2xl mx-auto p-5 lg:p-10">
        <button onClick={() => navigate(`/sites/${data.entreprise?.code || code}`)} data-testid="rv-back-link" className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white flex items-center gap-1 mb-4"><ArrowLeft className="h-3 w-3" />{data.entreprise?.name}</button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700] font-bold mb-2">// Votre avis</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Comment s'est passé votre séjour{data.client_prenom ? `, ${data.client_prenom}` : ""} ?</h1>
        <p className="text-base text-white/60 mt-3" data-testid="rv-stay">{data.unit_label} · du {fshort(data.date_from)} au {fshort(data.date_to)} · {data.entreprise?.name}</p>

        {data.submitted ? (
          <div className="mt-8 border border-[#059669] bg-[#059669]/10 p-6" data-testid="rv-done">
            <div className="flex items-center gap-2 text-[#059669] font-bold"><CheckCircle2 className="h-5 w-5" /> Merci ! Votre avis a bien été enregistré.</div>
            <div className="flex gap-1 mt-3" data-testid="rv-done-rating">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-6 w-6 ${i <= (data.rating || 0) ? "fill-[#FFD700] text-[#FFD700]" : "text-white/20"}`} />)}</div>
            {data.comment && <p className="text-sm text-white/70 mt-3 italic">« {data.comment} »</p>}
            {data.reply && <div className="mt-4 border-l-2 border-[#FFD700] pl-3 text-sm"><div className="text-[10px] uppercase tracking-wider text-[#FFD700] font-bold">Réponse de l'établissement</div><p className="text-white/80 mt-1">{data.reply}</p></div>}
            <div className="text-[11px] text-white/40 mt-4">L'établissement décide de la publication des avis sur son site.</div>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            <div className="border border-white/15 bg-white/5 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 mb-3">Votre note</div>
              <div className="flex items-center gap-2" data-testid="rv-stars" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} type="button" onClick={() => setRating(i)} onMouseEnter={() => setHover(i)} data-testid={`rv-star-${i}`} aria-label={`${i} étoile${i > 1 ? "s" : ""}`}
                    className="transition-transform hover:scale-110">
                    <Star className={`h-10 w-10 ${i <= (hover || rating) ? "fill-[#FFD700] text-[#FFD700]" : "text-white/25"}`} />
                  </button>
                ))}
                <span className="ml-3 text-sm text-white/70 min-w-[90px]" data-testid="rv-rating-label">{LABELS[hover || rating] || ""}</span>
              </div>
            </div>
            <div className="border border-white/15 bg-white/5 p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 mb-3">Votre commentaire (optionnel)</div>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 2000))} rows={5} data-testid="rv-comment" placeholder="Accueil, propreté, emplacement, équipements… ce qui vous a plu ou ce que nous pouvons améliorer." className="bg-white/5 border-white/20 text-white placeholder:text-white/30" />
              <div className="text-[10px] text-white/40 mt-1 text-right">{comment.length}/2000</div>
            </div>
            <Button onClick={submit} disabled={sending || !rating} data-testid="rv-submit" className="h-12 px-8 bg-[#FFD700] hover:bg-[#E6C200] text-black font-bold">{sending ? "Envoi…" : "Envoyer mon avis"}</Button>
            <div className="text-[11px] text-white/40">Un seul avis par séjour. Votre nom apparaîtra sous la forme « Prénom N. » si l'établissement publie votre avis.</div>
          </div>
        )}
      </div>
    </div>
  );
}
